import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { APP_NAME } from '../constants';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';
import AuthLayout from '../components/AuthLayout';
import Input from '../components/Input';
import Button from '../components/Button';

const Title = styled.h1`
  font-size: 28px;
  margin-bottom: 8px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
`;

const Subtitle = styled.p`
  font-size: 16px;
  margin-bottom: 30px;
  color: #555;
  font-family: 'Montserrat', sans-serif;
`;

const FormContainer = styled.div`
  width: 100%;
`;

const InfoText = styled.div`
  margin-top: 24px;
  text-align: center;
  font-family: 'Montserrat', sans-serif;
`;

const StyledLink = styled(Link)`
  color: black;
  text-decoration: underline;
  font-weight: 600;
  
  &:hover {
    text-decoration: underline;
  }
`;

const ErrorMessage = styled.p`
  color: #e53935;
  margin-bottom: 15px;
  font-size: 14px;
  font-family: 'Montserrat', sans-serif;
`;

const SuccessMessage = styled.p`
  color: #43a047;
  margin-bottom: 15px;
  font-size: 14px;
  font-family: 'Montserrat', sans-serif;
`;

const PasswordRequirements = styled.div`
  margin-bottom: 20px;
  padding: 15px;
  background-color: #f5f5f5;
  border-radius: 5px;
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
`;

const RequirementItem = styled.div<{ met: boolean }>`
  color: ${props => props.met ? '#43a047' : '#666'};
  margin-bottom: 5px;
  
  &:before {
    content: '${props => props.met ? '✓' : '•'}';
    margin-right: 8px;
    font-weight: bold;
  }
`;

// Initialize Cloud Function
const confirmPasswordResetFunction = httpsCallable(functions, 'confirmPasswordReset');

const ResetPasswordConfirmScreen: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing reset token');
    }
  }, [token]);

  // Password validation
  const passwordRequirements = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /\d/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
  };

  const isPasswordValid = Object.values(passwordRequirements).every(req => req);
  const passwordsMatch = password === confirmPassword && password.length > 0;

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    
    // Clear messages when user types
    if (error) setError('');
    if (success) setSuccess('');
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value);
    
    // Clear messages when user types
    if (error) setError('');
    if (success) setSuccess('');
  };

  const handleResetPassword = async () => {
    if (!token) {
      setError('Invalid reset token');
      return;
    }

    if (!isPasswordValid) {
      setError('Please ensure your password meets all requirements');
      return;
    }

    if (!passwordsMatch) {
      setError('Passwords do not match');
      return;
    }
    
    try {
      setLoading(true);
      
      // Call backend function to reset password
      const result = await confirmPasswordResetFunction({ 
        token, 
        newPassword: password 
      });
      
      setSuccess('Your password has been reset successfully! Redirecting to login...');
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (error: any) {
      console.error('Password reset confirmation error:', error);
      
      if (error.message.includes('expired')) {
        setError('This reset link has expired. Please request a new password reset.');
      } else if (error.message.includes('invalid') || error.message.includes('not-found')) {
        setError('Invalid or expired reset link. Please request a new password reset.');
      } else {
        setError('Failed to reset password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <>
        <Helmet>
          <title>{APP_NAME} - Invalid Reset Link</title>
          <meta name="description" content={`${APP_NAME} - Invalid Reset Link`} />
        </Helmet>
        <AuthLayout>
          <Title>Invalid Reset Link</Title>
          <Subtitle>This password reset link is invalid or has expired</Subtitle>
          <InfoText>
            <StyledLink to="/reset-password">Request a new password reset</StyledLink>
          </InfoText>
        </AuthLayout>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{APP_NAME} - Reset Password</title>
        <meta name="description" content={`${APP_NAME} - Reset Password`} />
      </Helmet>
      <AuthLayout>
        <Title>Reset Password</Title>
        <Subtitle>Enter your new password</Subtitle>
        
        <FormContainer>
          <Input
            type="password"
            name="password"
            value={password}
            onChange={handlePasswordChange}
            placeholder="Enter new password"
            label="New Password"
            required
          />

          <PasswordRequirements>
            <div style={{ marginBottom: '10px', fontWeight: 'bold' }}>Password Requirements:</div>
            <RequirementItem met={passwordRequirements.length}>
              At least 8 characters long
            </RequirementItem>
            <RequirementItem met={passwordRequirements.uppercase}>
              Contains uppercase letter (A-Z)
            </RequirementItem>
            <RequirementItem met={passwordRequirements.lowercase}>
              Contains lowercase letter (a-z)
            </RequirementItem>
            <RequirementItem met={passwordRequirements.number}>
              Contains number (0-9)
            </RequirementItem>
            <RequirementItem met={passwordRequirements.special}>
              Contains special character (!@#$%^&*(),.?":{}|&lt;&gt;)
            </RequirementItem>
          </PasswordRequirements>
          
          <Input
            type="password"
            name="confirmPassword"
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            placeholder="Confirm new password"
            label="Confirm Password"
            required
          />
          
          {confirmPassword && !passwordsMatch && (
            <ErrorMessage>Passwords do not match</ErrorMessage>
          )}
          
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}
          
          <Button 
            text="Reset Password"
            onClick={handleResetPassword}
            loading={loading}
            disabled={!isPasswordValid || !passwordsMatch || !token}
          />
        </FormContainer>
        
        <InfoText>
          Remember your password? <StyledLink to="/login">Back to Sign In</StyledLink>
        </InfoText>
      </AuthLayout>
    </>
  );
};

export default ResetPasswordConfirmScreen;
